import { useMemo, useState } from 'react';

import {
  AssetDTO,
  FormDTO,
  FormElementKind,
  IssueDTO,
  IssueStatus,
  PassFailCheckFormElementAnswerSchema,
  PassFailCheckFormElement as PassFailCheckFormElementType,
  PassFailWithNaCheckFormElementAnswerSchema,
  PassFailWithNaCheckFormElement as PassFailWithNaCheckFormElementType,
  RecordState,
} from '@gearbelt/global';
import { Button } from '@gearbelt/ui/components';
import { useQuery } from '@tanstack/react-query';
import { match } from 'ts-pattern';
import { z } from 'zod';

import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { RichText } from '@/components/ui/rich-text';
import { cn } from '@/lib/utils';
import { getAssetIssuesQueryOptions } from '@/queries/issues';
import { useIsAWA } from '@/utils/awa';

import { ConfigLabel } from '../config-label';
import { FieldShell } from '../field-shell';
import { PREVIEW_ASSET_ID } from '../preview-asset';
import { IssuesDisplay } from './issues-display';

// Special string to represent issue selection (will be converted to null internally)
const ISSUE_VALUE = '__ISSUE__';

const buttonKey = {
  pass: 'pass',
  na: 'na',
};

const getButtonOptionClassName = (position: 'left' | 'middle' | 'right', buttonValue: string | undefined, selected: boolean) => {
  const baseClassName = 'min-w-[100px]';

  const positionClassName = match(position)
    .with('left', () => 'border-y-[2px] border-l-[2px] border-r-none rounded-l-[5px] rounded-r-none border-grey-3')
    .with('middle', () => 'border-y-[2px] border-l-none border-r-none rounded-none border-grey-3')
    .with('right', () => 'border-y-[2px] border-l-none border-r-[2px] rounded-l-none rounded-r-[5px] border-grey-3')
    .exhaustive();

  const borderColorClassName = match(buttonValue)
    .with('pass', () => (selected ? 'border-default-green hover:border-secondary-green' : 'border-grey-3'))
    .with(ISSUE_VALUE, () => (selected ? 'border-default-red hover:border-secondary-red' : 'border-grey-3'))
    .with('na', () => (selected ? 'border-default-blue hover:border-secondary-blue' : 'border-grey-3'))
    .otherwise(() => 'border-grey-3');

  return cn(baseClassName, positionClassName, borderColorClassName);
};

const renderButtons = (
  element: PassFailWithNaCheckFormElementType | PassFailCheckFormElementType,
  buttonValue: string | undefined,
  handleButtonClick: (buttonValue: string) => void,
  readonly: boolean,
) => {
  return match(element.kind)
    .with(FormElementKind.PassFailWithNaCheck, () => {
      return (
        <>
          <Button
            type="button"
            variant={buttonValue === buttonKey.pass ? 'success' : 'outline'}
            aria-pressed={buttonValue === buttonKey.pass}
            className={getButtonOptionClassName('left', buttonValue, buttonValue === buttonKey.pass)}
            disabled={readonly}
            onClick={() => handleButtonClick(buttonKey.pass)}>
            Pass
          </Button>
          <Button
            type="button"
            variant={buttonValue === ISSUE_VALUE ? 'destructive' : 'outline'}
            aria-pressed={buttonValue === ISSUE_VALUE}
            className={getButtonOptionClassName('middle', buttonValue, buttonValue === ISSUE_VALUE)}
            disabled={readonly}
            onClick={() => handleButtonClick(ISSUE_VALUE)}>
            Fail
          </Button>
          <Button
            type="button"
            variant={buttonValue === buttonKey.na ? 'default' : 'outline'}
            aria-pressed={buttonValue === buttonKey.na}
            className={getButtonOptionClassName('right', buttonValue, buttonValue === buttonKey.na)}
            disabled={readonly}
            onClick={() => handleButtonClick(buttonKey.na)}>
            NA
          </Button>
        </>
      );
    })
    .with(FormElementKind.PassFailCheck, () => {
      return (
        <>
          <Button
            type="button"
            variant={buttonValue === buttonKey.pass ? 'success' : 'outline'}
            aria-pressed={buttonValue === buttonKey.pass}
            className={getButtonOptionClassName('left', buttonValue, buttonValue === buttonKey.pass)}
            disabled={readonly}
            onClick={() => handleButtonClick(buttonKey.pass)}>
            Pass
          </Button>
          <Button
            type="button"
            variant={buttonValue === ISSUE_VALUE ? 'destructive' : 'outline'}
            aria-pressed={buttonValue === ISSUE_VALUE}
            className={getButtonOptionClassName('right', buttonValue, buttonValue === ISSUE_VALUE)}
            disabled={readonly}
            onClick={() => handleButtonClick(ISSUE_VALUE)}>
            Fail
          </Button>
        </>
      );
    })
    .otherwise(() => {
      return null;
    });
};

export function PassFailCheckFormElement({
  element,
  value,
  onChange,
  onBlur,
  readonly,
  isPreview = false,
  asset,
  form,
  hideLabel,
  error,
  locationId,
}: {
  element: PassFailWithNaCheckFormElementType | PassFailCheckFormElementType;
  value?: z.infer<typeof PassFailCheckFormElementAnswerSchema>['value'] | z.infer<typeof PassFailWithNaCheckFormElementAnswerSchema>['value'];
  onChange: (
    value: z.infer<typeof PassFailCheckFormElementAnswerSchema>['value'] | z.infer<typeof PassFailWithNaCheckFormElementAnswerSchema>['value'],
  ) => void;
  onBlur?: () => void;
  readonly: boolean;
  isPreview?: boolean;
  asset: AssetDTO;
  locationId?: string;
  form: FormDTO;
  hideLabel?: boolean;
  error?: string;
}) {
  const { isAWA } = useIsAWA();

  // Readonly path fetches only the saved issue IDs without an explicit state filter —
  // backend default (Active) applies. If product confirms archived issues should be
  // visible in historical check forms, pass issueState: [Active, Archived] here.
  const issueFilter =
    readonly && value?.issues
      ? { issueId: value.issues }
      : { issueStatus: [IssueStatus.Open], issueState: [RecordState.Active] as (RecordState.Active | RecordState.Archived)[] };
  const { data: issuesData } = useQuery({
    ...getAssetIssuesQueryOptions({ assetId: asset.id, isAWA, filters: issueFilter }),
    enabled: !!asset.id && asset.id !== PREVIEW_ASSET_ID,
  });

  const [issues, setIssues] = useState<IssueDTO[]>([]);

  const relevantIssues = useMemo(() => {
    const fetchedIssues = issuesData ?? [];
    const fetchedIssueIds = new Set(fetchedIssues.map((issue) => issue.id));

    const uniqueLocalIssues = issues.filter((issue) => !fetchedIssueIds.has(issue.id));

    const allIssues = [...fetchedIssues, ...uniqueLocalIssues];
    return allIssues.filter((issue) => !element.id || issue.checkFormElementId === element.id);
  }, [issuesData, issues, element.id]);

  const handleIssuesChange = (newIssue: IssueDTO) => {
    setIssues((prev) => [...prev, newIssue]);

    const currentIssueIds = value?.issues || [];
    onChange({
      option: null,
      issues: [...currentIssueIds, newIssue.id],
    });
    if (error) {
      onBlur?.();
    }
  };

  const handleButtonClick = (buttonValue: string) => {
    if (buttonValue === ISSUE_VALUE) {
      onChange({
        option: null,
        issues: relevantIssues.map((issue) => issue.id),
      });
    } else {
      if (element.kind === FormElementKind.PassFailWithNaCheck) {
        onChange({
          option: buttonValue as 'pass' | 'na' | null,
          issues: null,
        });
      } else if (element.kind === FormElementKind.PassFailCheck) {
        onChange({
          option: buttonValue as 'pass' | null,
          issues: null,
        });
      }
    }
    if (error) {
      onBlur?.();
    }
  };

  // Determine the Button value
  const buttonValue = useMemo(() => value?.option || (value?.issues ? ISSUE_VALUE : undefined), [value]);

  return (
    <FieldShell label={element.label} required={element.required} description={element.description} hideLabel={hideLabel} error={error}>
      <div className="space-y-[10px]">
        <div className="flex justify-center">{renderButtons(element, buttonValue, handleButtonClick, readonly)}</div>
        {(relevantIssues.length > 0 || (value?.issues !== null && value?.issues !== undefined)) && (
          <IssuesDisplay
            asset={asset}
            form={form}
            checkId={element.id}
            locationId={locationId}
            readonly={readonly || isPreview}
            requirePhotos={element.config?.issue.requirePhotos ?? false}
            showNewIssueButton={!readonly}
            issues={relevantIssues}
            onIssuesChange={handleIssuesChange}
            fieldReference={element.label}
          />
        )}
      </div>
    </FieldShell>
  );
}

export function PassFailCheckFormElementConfig({
  element,
  onChange,
}: {
  element: PassFailCheckFormElementType | PassFailWithNaCheckFormElementType;
  onChange?: (updates: Partial<PassFailCheckFormElementType | PassFailWithNaCheckFormElementType>) => void;
}) {
  return (
    <div className="flex flex-col gap-4">
      <ConfigLabel>
        <span>Label:</span>
        <Input value={element.label} onChange={(e) => onChange?.({ label: e.target.value })} />
      </ConfigLabel>

      <ConfigLabel noLabel>
        <span>Question:</span>
        <RichText value={element.description} onChange={(value) => onChange?.({ description: value.toString() })} />
      </ConfigLabel>

      <ConfigLabel className="flex-row items-start gap-2">
        <Checkbox checked={element.required} onCheckedChange={(checked) => onChange?.({ required: !!checked })} />
        <span>Required</span>
      </ConfigLabel>
    </div>
  );
}
